import { useEffect, useState } from 'react';
import Divider from 'ui/ds/Divider';
import { RHIFailedIllustration } from 'ui/icons';

import { RHI_TIMEOUT_VALUES } from '@/constants/queryKeys';
import {
  endAgentSession,
  getAgentSession,
  getRedeemFailure,
  isAgentLaunch,
  type RedeemFailure,
} from '@/utils/agentSession';

export const AgentSessionBar = () => {
  if (!isAgentLaunch()) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 border-b border-retail-accent-orange bg-retail-accent-orange-bg px-5 py-4 lg:px-[180px]">
      <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-retail-accent-orange" />
      <span className="font-chromatica text-base font-black text-retail-text-primary lg:text-lg">
        Agent session · Superleap
      </span>
    </div>
  );
};

export const AgentSessionExpiryWarning = () => {
  const [isVisible, setIsVisible] = useState(false);
  const expiresAt = getAgentSession()?.expiresAt;
  const expiresAtMs = expiresAt ? Date.parse(expiresAt) : NaN;
  const hasValidExpiry = !Number.isNaN(expiresAtMs);

  useEffect(() => {
    if (!isAgentLaunch() || !hasValidExpiry) return;

    const timeUntilExpiry = expiresAtMs - Date.now();
    const delay =
      timeUntilExpiry - RHI_TIMEOUT_VALUES.AGENT_SESSION_WARNING_LEAD_MS;

    if (timeUntilExpiry <= 0) {
      endAgentSession();
      return;
    }

    const expiryTimer = setTimeout(endAgentSession, timeUntilExpiry);

    if (delay <= 0) {
      setIsVisible(true);
      return () => clearTimeout(expiryTimer);
    }

    const warningTimer = setTimeout(() => setIsVisible(true), delay);

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(expiryTimer);
    };
  }, [expiresAtMs, hasValidExpiry]);

  if (!isVisible || !hasValidExpiry) {
    return null;
  }

  const endsAt = new Date(expiresAtMs).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div
      role="status"
      className="border-b border-retail-accent-orange bg-retail-accent-orange-bg px-5 py-3 lg:px-[180px]"
    >
      <p className="text-sm font-semibold text-retail-text-primary lg:text-base">
        This session ends at {endsAt}. Your progress is saved: relaunch from
        your CRM and pick it up from the journey list.
      </p>
    </div>
  );
};

type Copy = {
  title: string;
  message: string;
};

const SESSION_ENDED: Copy = {
  title: 'Start again from your CRM',
  message:
    'This session has ended. Your progress is saved: relaunch from your CRM and continue it from the journey list.',
};

const REDEEM_FAILURE_COPY: Record<RedeemFailure, Copy> = {
  INVALID_CODE: {
    title: 'This link has expired',
    message: 'Please reopen the journey from your CRM.',
  },
  AGENT_NOT_AUTHORISED: {
    title: 'Access removed',
    message:
      'Your access to retail journeys has been removed. Contact your administrator.',
  },
  AGENT_NOT_FOUND: {
    title: 'Access removed',
    message:
      'Your access to retail journeys has been removed. Contact your administrator.',
  },
  MINT_FAILED: {
    title: 'Something went wrong',
    message: 'Please reopen the journey from your CRM.',
  },
  STORE_UNAVAILABLE: {
    title: 'Temporarily unavailable',
    message: 'Please reopen the journey from your CRM to try again.',
  },
  RATE_LIMITED: {
    title: 'Too many attempts',
    message: 'Please wait a moment, then reopen the journey from your CRM.',
  },
  UNKNOWN: {
    title: 'Something went wrong',
    message: 'Please reopen the journey from your CRM.',
  },
};

export const AgentJourneyEnded = () => {
  const failure = getRedeemFailure();
  const { title, message } = failure
    ? REDEEM_FAILURE_COPY[failure]
    : SESSION_ENDED;

  return (
    <div
      role="alert"
      className="min-h-screen flex flex-col justify-center items-center px-5"
    >
      <RHIFailedIllustration />
      <p className="text-retail-text-primary text-center font-chromatica text-xl font-black mt-4">
        {title}
      </p>
      <p className="text-retail-text-secondary text-center text-base lg:text-lg font-normal mt-1 lg:w-[30%] mx-auto">
        {message}
      </p>
      <Divider wrapperClassName="w-[30%] lg:w-[60px] h-1 mx-auto my-4" />
    </div>
  );
};
